import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Chip, CircularProgress, Collapse, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import colors from 'common/constants/colors';
import {
  LINK_CHECK_STATUS,
  countLabel,
  firstWordOf,
  groupSimilarProducts,
  listStrengthFormPack,
  productNameOf,
} from '../utils/ndcProductLink';
import LinkFactChip from './LinkFactChip';
import LinkedNdcRows from './LinkedNdcRows';
import ProductIdChip from './ProductIdChip';

// Manufacturer belongs to an NDC, not to a product, so it is shown on the NDC rows only.
const SimilarProductRow = ({ product }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      data-testid='similar-product'
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: colors.cardBorderGrey,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}
      >
        <Typography variant='body2' sx={{ fontWeight: 700 }}>
          {productNameOf(product)}
        </Typography>
        <ProductIdChip productId={product.productId} />
      </Box>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.25 }}>
        {listStrengthFormPack(product)
          .map(({ label, value }) => `${label} ${value}`)
          .join(' · ')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
        {product.tags.map((tag) => (
          <LinkFactChip key={tag} fact={tag} />
        ))}
        <Box sx={{ ml: 'auto' }}>
          {product.ndcs.length > 0 ? (
            <Button
              size='small'
              onClick={() => setIsExpanded((wasExpanded) => !wasExpanded)}
              endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              aria-expanded={isExpanded}
              sx={{ px: 0.5, minWidth: 0, fontWeight: 700, textTransform: 'none' }}
            >
              {countLabel(product.ndcs.length, 'NDC', 'NDCs')}
            </Button>
          ) : (
            <Typography variant='caption' color='text.secondary'>
              No NDCs yet
            </Typography>
          )}
        </Box>
      </Box>
      <Collapse in={isExpanded} unmountOnExit>
        <Box sx={{ mt: 1 }}>
          <LinkedNdcRows ndcs={product.ndcs} />
        </Box>
      </Collapse>
    </Box>
  );
};

const linkCheckProductShape = PropTypes.shape({
  catalogId: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  drugName: PropTypes.string,
  strength: PropTypes.string,
  dosageForm: PropTypes.string,
  formAlias: PropTypes.string,
  packSizeValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  packSizeUnit: PropTypes.string,
  ndcs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
});

SimilarProductRow.propTypes = {
  product: linkCheckProductShape.isRequired,
};

// POM-6600 — the right side of the review step: products a wrong pick is easily confused with.
// Nothing here is suggested or switched to.
const SimilarProductsPanel = ({ linkCheck, linkCheckStatus, onRetry, pickedDrugName }) => {
  const isLoaded = linkCheckStatus === LINK_CHECK_STATUS.LOADED && Boolean(linkCheck);
  const similarProducts = isLoaded ? linkCheck.similarProducts : [];
  const { differentName, sameDrugName } = groupSimilarProducts(similarProducts, pickedDrugName);
  const firstWord = firstWordOf(pickedDrugName);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: colors.cardBorderGrey,
        bgcolor: colors.dialogSurfaceGrey,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant='caption'
          sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}
        >
          SIMILAR PRODUCTS
        </Typography>
        {isLoaded && <Chip size='small' label={similarProducts.length} sx={{ height: 20 }} />}
      </Box>

      {linkCheckStatus === LINK_CHECK_STATUS.LOADING && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant='body2' color='text.secondary'>
            Checking similar products…
          </Typography>
        </Box>
      )}

      {linkCheckStatus === LINK_CHECK_STATUS.FAILED && (
        <Box>
          <Typography variant='body2' sx={{ color: colors.warningAlertText }}>
            Couldn&apos;t load the check. You can still confirm; compare the product with the bottle
            or vial label first.
          </Typography>
          <Button
            size='small'
            onClick={onRetry}
            startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{ px: 0, minWidth: 0 }}
          >
            Retry
          </Button>
        </Box>
      )}

      {isLoaded && (
        <>
          <Typography variant='body2' color='text.secondary'>
            Active products whose name starts with “{firstWord}”. Compare them with the bottle or
            vial label.
          </Typography>

          {similarProducts.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              No other active products start with “{firstWord}”.
            </Typography>
          )}

          {[
            { groupLabel: 'Different name', products: differentName },
            { groupLabel: 'Same drug name', products: sameDrugName },
          ]
            .filter(({ products }) => products.length > 0)
            .map(({ groupLabel, products }) => (
              <Box key={groupLabel} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 700 }}>
                  {groupLabel}
                </Typography>
                {products.map((product) => (
                  <SimilarProductRow key={product.catalogId} product={product} />
                ))}
              </Box>
            ))}
        </>
      )}
    </Box>
  );
};

SimilarProductsPanel.propTypes = {
  linkCheck: PropTypes.shape({
    similarProducts: PropTypes.arrayOf(linkCheckProductShape),
  }),
  linkCheckStatus: PropTypes.oneOf(Object.values(LINK_CHECK_STATUS)).isRequired,
  onRetry: PropTypes.func.isRequired,
  pickedDrugName: PropTypes.string.isRequired,
};
SimilarProductsPanel.defaultProps = {
  linkCheck: null,
};

export default SimilarProductsPanel;
